import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/Card';
import { PageHeader } from '../components/ui/PageHeader';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Badge } from '../components/ui/Badge';
import { apiService } from '../services/apiService';

interface IntCoinsTransaction {
  id: number;
  user: number;
  user_email: string;
  transaction_type: 'credit' | 'debit';
  amount: number;
  description: string;
  search_request: number | null;
  expiration_date: string | null;
  created_at: string;
}

export const AdminIntCoinsReportPage: React.FC = () => {
  const [transactions, setTransactions] = useState<IntCoinsTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'credit' | 'debit'>('all');

  useEffect(() => {
    const fetchTransactions = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiService.adminRequest<IntCoinsTransaction[]>(
          '/admin/intcoins-transactions/'
        );
        setTransactions(response);
      } catch (err: any) {
        setError(err.message || 'Failed to load transactions');
      } finally {
        setLoading(false);
      }
    };
    fetchTransactions();
  }, []);

  const filtered = transactions.filter(tx => {
    const matchesType = typeFilter === 'all' || tx.transaction_type === typeFilter;
    const matchesSearch =
      tx.user_email.toLowerCase().includes(search.toLowerCase()) ||
      tx.description.toLowerCase().includes(search.toLowerCase());
    return matchesType && matchesSearch;
  });

  const formatDate = (date: string | null) =>
    date ? new Date(date).toLocaleString('en-IN', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

  return (
    <div className="space-y-6">
      <PageHeader title="IntCoins Transactions Report" subtitle="Admin report of all IntCoins credits and debits" align="center" />
      <Card>
        <div className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex flex-wrap gap-2 items-center">
            <input
              type="text"
              placeholder="Search by user or description..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm w-64 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            />
            <select
              value={typeFilter}
              onChange={e => setTypeFilter(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            >
              <option value="all">All Types</option>
              <option value="credit">Credit</option>
              <option value="debit">Debit</option>
            </select>
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Total: {filtered.length} transactions</div>
        </div>
      </Card>
      <Card>
        <div className="overflow-x-auto custom-scrollbar">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <LoadingSpinner text="Loading transactions..." />
            </div>
          ) : error ? (
            <div className="p-4 text-red-600 dark:text-red-400">{error}</div>
          ) : (
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-100 dark:bg-gray-800">
                  <th className="px-4 py-2 text-left">User</th>
                  <th className="px-4 py-2 text-left">Type</th>
                  <th className="px-4 py-2 text-right">Amount</th>
                  <th className="px-4 py-2 text-left">Description</th>
                  <th className="px-4 py-2 text-left">Expiration</th>
                  <th className="px-4 py-2 text-left">Date</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(tx => (
                  <tr key={tx.id} className="border-b last:border-0">
                    <td className="px-4 py-2">{tx.user_email}</td>
                    <td className="px-4 py-2">
                      <Badge variant={tx.transaction_type === 'credit' ? 'success' : 'danger'}>
                        {tx.transaction_type}
                      </Badge>
                    </td>
                    <td className="px-4 py-2 text-right font-semibold">
                      {tx.amount}
                    </td>
                    <td className="px-4 py-2">{tx.description}</td>
                    <td className="px-4 py-2">{formatDate(tx.expiration_date)}</td>
                    <td className="px-4 py-2">{formatDate(tx.created_at)}</td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-gray-500 dark:text-gray-400">
                      No transactions found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </Card>
    </div>
  );
}; 